import React, { useRef, useState, useEffect } from 'react';
import { FixedSizeGrid as Grid } from 'react-window';
import { LotCard } from './LotCard';
import { LotDetailsModal } from './LotDetailsModal';

const COLUMN_WIDTH = 240; // Base width of each lot card to match image width plus padding
const ROW_HEIGHT = 395; // Height of each lot card
const GAP = 24; // Gap between cards

const Cell = React.memo(({ columnIndex, rowIndex, style, data }) => {
  const index = rowIndex * data.columnCount + columnIndex;
  if (index >= data.lots.length) return null;

  const lot = data.lots[index];
  if (!lot) return null;

  // Compute cell style directly since it's based on props
  const cellStyle = {
    ...style,
    padding: `${GAP/2}px`,
    paddingLeft: columnIndex === 0 ? 0 : GAP/2,
    paddingRight: columnIndex === data.columnCount - 1 ? 0 : GAP/2,
    width: data.adjustedColumnWidth + GAP,
  };

  // Handlers with stable references
  const handleSelect = React.useCallback(() => 
    data.onSelect(lot.id),
    [lot.id, data.onSelect]
  );

  const handleDeselect = React.useCallback(() => 
    data.onDeselect(lot.id),
    [lot.id, data.onDeselect]
  );

  const handleOpenModal = React.useCallback(() => 
    data.onOpenAuctionModal(lot.id), 
    [lot.id, data.onOpenAuctionModal]
  );

  const handleSelectAuction = React.useCallback(() =>
    data.onSelectAuction(lot.id),
    [lot.id, data.onSelectAuction]
  );

  return (
    <div style={cellStyle}>
      <div className="h-full">
        <LotCard
          lot={lot}
          auctionId={data.auctionId}
          auctionCurrency={data.auctionCurrency}
          isAuctionSelected={data.isAuctionSelected}
          certServices={data.certServices}
          onOpenAuctionModal={handleOpenModal}
          onSelectAuction={handleSelectAuction}
          onSelect={handleSelect}
          onDeselect={handleDeselect}
        />
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  const prevIndex = prevProps.rowIndex * prevProps.data.columnCount + prevProps.columnIndex;
  const nextIndex = nextProps.rowIndex * nextProps.data.columnCount + nextProps.columnIndex;
  
  // Only re-render if the lot at this position has changed
  const prevLot = prevProps.data.lots[prevIndex];
  const nextLot = nextProps.data.lots[nextIndex];
  
  return (
    prevLot?.id === nextLot?.id &&
    prevLot?.user_selection === nextLot?.user_selection &&
    prevProps.style === nextProps.style &&
    prevProps.data.isAuctionSelected === nextProps.data.isAuctionSelected &&
    prevProps.data.auctionCurrency === nextProps.data.auctionCurrency &&
    prevProps.data.certServices === nextProps.data.certServices
  );
});

const GridComponent = React.memo(({ 
  lots,
  columnCount,
  adjustedColumnWidth,
  rowCount,
  gridWidth,
  totalHeight,
  gridRef,
  ...rest
}) => {
  const itemData = {
    lots,
    columnCount,
    adjustedColumnWidth,
    ...rest
  };

  return (
    <Grid
      ref={gridRef}
      columnCount={columnCount}
      columnWidth={adjustedColumnWidth + GAP}
      height={totalHeight}
      rowCount={rowCount}
      rowHeight={ROW_HEIGHT}
      width={gridWidth}
      overscanColumnCount={2}
      overscanRowCount={3}
      style={{ overflow: 'visible', outline: 'none' }}
      tabIndex={-1}
      useIsScrolling={true}
      itemData={itemData}
    >
      {Cell}
    </Grid>
  );
});

export function VirtualizedLotGrid({ 
  lots, 
  auctionId, 
  auctionCurrency,
  isAuctionSelected,
  certServices,
  onOpenAuctionModal,
  onSelectAuction,
  onSelect, 
  onDeselect,
  containerRef,
  auctionName,
  auctionHouseName
}) {
  const [selectedLot, setSelectedLot] = useState(null);
  const gridRef = useRef();
  const [gridWidth, setGridWidth] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      const updateWidth = () => {
        const rect = containerRef.current.getBoundingClientRect();
        setGridWidth(rect.width);
      };

      updateWidth();
      const resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(containerRef.current);

      return () => resizeObserver.disconnect();
    }
  }, [containerRef]);

  // Wrap handlers to update selectedLot state
  const handleSelect = React.useCallback((lotId) => {
    onSelect(lotId);
    setSelectedLot(prev => {
      if (prev?.id === lotId) {
        return {
          ...prev,
          user_selection: true
        };
      }
      return prev;
    });
  }, [onSelect]);

  const handleDeselect = React.useCallback((lotId) => {
    onDeselect(lotId);
    setSelectedLot(prev => {
      if (prev?.id === lotId) {
        return {
          ...prev,
          user_selection: false
        };
      }
      return prev;
    });
  }, [onDeselect]);

  if (!gridWidth) return null;

  // Calculate grid dimensions to fill width completely
  const columnsFloat = gridWidth / (COLUMN_WIDTH + GAP);
  const columnCount = Math.max(1, Math.floor(columnsFloat));
  const adjustedColumnWidth = Math.floor(gridWidth / columnCount - GAP);

  const rowCount = Math.ceil(lots.length / columnCount);
  const totalHeight = rowCount * ROW_HEIGHT;

  return (
    <>
      <LotDetailsModal
        lot={selectedLot}
        auctionCurrency={auctionCurrency}
        certServices={certServices}
        isOpen={Boolean(selectedLot)}
        onClose={() => setSelectedLot(null)}
        auctionName={auctionName}
        auctionHouseName={auctionHouseName}
        isAuctionSelected={isAuctionSelected}
        onSelect={handleSelect}
        onDeselect={handleDeselect}
        onSelectAuction={onSelectAuction}
      />
      <div className="w-full relative focus:outline-none focus-visible:outline-none" style={{ height: totalHeight, outline: 'none' }}>
        <div className="focus:outline-none focus-visible:outline-none" style={{ outline: 'none' }}>
          <GridComponent
            lots={lots}
            columnCount={columnCount}
            adjustedColumnWidth={adjustedColumnWidth}
            rowCount={rowCount}
            gridWidth={gridWidth}
            totalHeight={totalHeight}
            gridRef={gridRef}
            auctionId={auctionId}
            auctionCurrency={auctionCurrency}
            isAuctionSelected={isAuctionSelected}
            certServices={certServices}
            onOpenAuctionModal={(lotId) => {
              const lot = lots.find(l => l.id === lotId);
              if (lot) setSelectedLot(lot);
            }}
            onSelectAuction={onSelectAuction}
            onSelect={handleSelect}
            onDeselect={handleDeselect}
          />
        </div>
      </div>
    </>
  );
}
